import {
  VoiceProfileDetailApi,
  VoiceProfileCreateApi,
  VoiceProfileListItemApi,
  VoiceProfileUpdateApi,
  RealtimeSessionShortApi,
} from '../Services/VoiceProfileApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

// ============================================================
// Types
// ============================================================

export type VoiceProfileForm = {
  name: string;
  assistantKey: string;
  voice: string;
  voiceSpeed: number;
  vadThreshold: number;
  vadSilence: number;
  isActive: boolean;
};

type Agent = {
  key: string;
  name: string;
};

type VoiceOption = {
  value: string;
  label: string;
};

export type VoiceProfileFormProps = {
  form: VoiceProfileForm;
  agents: Agent[];
  voices: VoiceOption[];
  isDisabled?: boolean;
  onChange: (form: VoiceProfileForm) => void;
};

// ============================================================
// Constants
// ============================================================

export const INITIAL_FORM_STATE: VoiceProfileForm = {
  name: '',
  assistantKey: '',
  voice: '',
  voiceSpeed: 1,
  vadThreshold: 0.5,
  vadSilence: 1,
  isActive: true,
};

export const VOICE_PROFILE_FIELD_MAP: Record<keyof VoiceProfileForm, keyof VoiceProfileDetailApi> =
  {
    name: 'name',
    assistantKey: 'assistant_key',
    voice: 'voice',
    voiceSpeed: 'voice_speed',
    vadThreshold: 'vad_threshold',
    vadSilence: 'vad_silence',
    isActive: 'is_active',
  };

// Fields that should be sent as `null` when empty (nullable string fields)
const NULLABLE_STRING_FIELDS: (keyof VoiceProfileForm)[] = ['assistantKey', 'voice'];

// ============================================================
// Table columns
// ============================================================

export const voiceProfileColumns = [
  { name: 'ID', selector: (row: VoiceProfileListItemApi) => row.key, sortable: false },
  { name: 'Name', selector: (row: VoiceProfileListItemApi) => row.name, sortable: false },
  {
    name: 'Status',
    selector: (row: VoiceProfileListItemApi) => (row.is_active ? 'Active' : 'InActive'),
    sortable: false,
  },
  {
    name: 'Created',
    selector: (row: VoiceProfileListItemApi) => formatDateUTC(row.created_at),
    sortable: false,
  },
  {
    name: 'Updated',
    selector: (row: VoiceProfileListItemApi) => formatDateUTC(row.updated_at),
    sortable: false,
  },
];

// ============================================================
// Session helpers
// ============================================================

export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
};

export const sessionColumns = [
  { name: 'ID', selector: (row: RealtimeSessionShortApi) => row.key, sortable: false },
  {
    name: 'Created',
    selector: (row: RealtimeSessionShortApi) => formatDateUTC(row.created_at),
    sortable: false,
  },
  {
    name: 'Duration',
    selector: (row: RealtimeSessionShortApi) => formatDuration(row.duration_seconds),
    sortable: false,
  },
];

// ============================================================
// Validation
// ============================================================

export const validate = (
  form: VoiceProfileForm,
  showAlert: (type: 'fail' | 'success', message: string) => void,
): boolean => {
  const checks: [boolean, string][] = [[!form.name.trim(), 'Voice profile name is required']];

  const failed = checks.find(([condition]) => condition);
  if (failed) {
    showAlert('fail', failed[1]);
    return false;
  }

  return true;
};

// ============================================================
// Form <-> API mapping
// ============================================================

const toApiValue = (
  formKey: keyof VoiceProfileForm,
  value: VoiceProfileForm[keyof VoiceProfileForm],
) => {
  if (NULLABLE_STRING_FIELDS.includes(formKey)) {
    return (value as string) || null;
  }
  return value;
};

export const mapFormToApi = (form: VoiceProfileForm): VoiceProfileCreateApi => {
  return Object.entries(VOICE_PROFILE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    const key = formKey as keyof VoiceProfileForm;
    (acc as Record<string, unknown>)[apiKey] = toApiValue(key, form[key]);
    return acc;
  }, {} as VoiceProfileCreateApi);
};

export const getChangedFields = (
  initial: VoiceProfileForm,
  current: VoiceProfileForm,
): VoiceProfileUpdateApi => {
  return Object.entries(VOICE_PROFILE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    const key = formKey as keyof VoiceProfileForm;
    if (current[key] !== initial[key]) {
      (acc as Record<string, unknown>)[apiKey] = toApiValue(key, current[key]);
    }
    return acc;
  }, {} as VoiceProfileUpdateApi);
};
